<template>
  <div>
    <div class="m-4">
      <v-client-table
        :columns="getColumns()"
        :data="allocations"
        :options="options"
      >
        <!-- FUND -->
        <template v-slot:fund="{ row }">
          <span class="table-main-class">
            {{ row.fund.name }}
          </span>
        </template>

        <!-- RULE -->
        <template v-slot:rule="{ row }">
          <Multiselect
            v-if="allocationsHash[row.id] && isDraft"
            :options="getRules(row)"
            :placeholder="$t('selectRule') | capitalize"
            label="name"
            :multiple="false"
            :show-labels="false"
            v-model="allocationsHash[row.id].rule"
          />
          <template v-else>
            <template v-if="hasRule(row)">
              {{ allocationsHash[row.id].rule.name }}
            </template>
          </template>
        </template>

        <!-- USE TYPE -->
        <template v-slot:use_type="{ row }">
          <Multiselect
            v-if="!isUPA(row) && hasRule(row) && isDraft"
            :options="rights"
            :placeholder="$t('selectUseType') | capitalize"
            label="name"
            :multiple="false"
            :show-labels="false"
            v-model="allocationsHash[row.id].right_type"
          />
          <template v-else>
            <template v-if="hasUseType(row)">
              {{ allocationsHash[row.id].right_type.name }}
            </template>
          </template>
        </template>

        <!--   STATUS   -->
        <template v-slot:status="{ row }">
          <Status
              :text="$t(row.status) | capitalize"
              :icon="['fas', 'check-circle']"
              v-if="row.status === 'allocated'"
          color="green"
          :active="true"
          />
          <Status
              :text="$t(row.status) | capitalize"
              :icon="['fas', 'exclamation-circle']"
              v-else-if="row.status === statusTypes.ERROR"
          color="red"
          :active="true"
          @click="showErrors(row, true)"
          clickable
          />
          <Status
              :text="$t(row.status) | capitalize"
              :icon="['fas', 'question-circle']"
              v-else
          color="orange"
          :active="true"
          />
        </template>

        <!-- DEDUCTION % -->
        <template v-slot:deduction="{ row }">
          <input type="number" v-if="isIPAB(row) && hasRule(row) && isDraft"
            v-model="allocationsHash[row.id].deduction"
            max=100
            min=0
            class="form-control"
          />
          <template v-else>
            <template v-if="hasUseType(row) && isIPAB(row)">
              {{ allocationsHash[row.id].deduction }}
            </template>
          </template>
        </template>

        <!-- ADDITIONAL DATA -->
        <template v-slot:additional_data="{ row }">
          <!-- PRO-RATA  -->
          <template v-if="isProRata(row) && hasUseType(row)">
            <b-button
              v-if="!hasProRataFile(row)"
              variant="outline-primary"
              size="lg"
              @click="showDropzone(row)"
            >
              {{ $t('uploadProRataFile') | capitalize }}
            </b-button>

            <div v-else class="d-flex justify-content-between align-items-center">
              <div class="italic">
                {{ allocationsHash[row.id].pro_rata_file.filename }}
              </div>
              <div v-if="isDraft" class="d-flex">
                <fa-icon
                  @click.stop="removeProRataFile(row)"
                  :icon="['fas', 'trash']"
                  class="clickable ml-4 text-muted"
                />
                <fa-icon
                  @click.stop="showDropzone(row)"
                  :icon="['fas', 'pen']"
                  class="clickable ml-4 text-muted"
                />
              </div>
            </div>
          </template>

          <!-- UPA -->
          <template v-if="isUPA(row)">
            <b-button
              v-if="hasUPAMembers(row)"
              v-b-modal="'viewUPAMembersModal'"
              variant="primary"
              size="lg"
              @click="selectedAllocation = allocationsHash[row.id]"
            >
              {{ $t('view_selected_members') | capitalize }}
            </b-button>

            <b-button
              v-else
              v-b-modal="'viewUPAMembersModal'"
              variant="outline-primary"
              size="lg"
              @click="selectedAllocation = allocationsHash[row.id]"
            >
              {{ $t('select_members') | capitalize }}
            </b-button>
          </template>

          <!--OTHER RULES -->
          <template v-if="hasRule(row) && !isUPA(row) && !isProRata(row)">
            <template v-if="isDraft">
              <b-button
                v-if="hasPlaylists(row)"
                v-b-modal="'playlistSelectorModal'"
                variant="primary"
                size="lg"
                @click="selectedAllocation = allocationsHash[row.id]"
              >
                {{ $t('view_selected_playlists') | capitalize }}
              </b-button>

              <b-button
                v-else
                v-b-modal="'playlistSelectorModal'"
                variant="outline-primary"
                size="lg"
                @click="selectedAllocation = allocationsHash[row.id]"
              >
                {{ $t('selectPlaylists') | capitalize }}
              </b-button>

              <b-button
                v-if="hasExcludedMembers(row)"
                v-b-modal="'viewUsageMembersModal'"
                variant="primary"
                size="lg"
                @click="selectedAllocation = allocationsHash[row.id]"
              >
                {{ $t('view_excluded_members') | capitalize }}
              </b-button>

              <b-button
                v-else
                v-b-modal="'viewUsageMembersModal'"
                variant="outline-primary"
                size="lg"
                @click="selectedAllocation = allocationsHash[row.id]"
              >
                {{ $t('exclude_members') | capitalize }}
              </b-button>
            </template>
          <div v-else class="d-flex justify-content-between align-items-center">
            <b-button
              v-b-modal="'viewPlaylistsModal'"
              variant="primary"
              size="lg"
              @click="selectedAllocation = allocationsHash[row.id]"
            >
              {{ $t('view_selected_playlists') | capitalize }}
            </b-button>
            <b-button
              v-if="hasExcludedMembers(row)"
              v-b-modal="'viewUsageMembersModal'"
              variant="primary"
              size="lg"
              @click="selectedAllocation = allocationsHash[row.id]"
            >
              {{ $t('view_excluded_members') | capitalize }}
            </b-button>
          </div>
          </template>
        </template>
      </v-client-table>
    </div>

    <!-- UPA MEMBERS MODAL -->
    <b-modal
      id="viewUPAMembersModal"
      ref="viewUPAMembersModal"
      hide-footer
      hide-header
      size="xl"
    >
      <MembersSelector
        :key="selectedAllocation.id"
        :allocation="selectedAllocation"
        :editable="isDraft"
        @save="setUPAMembers"
        @close="closeModal('viewUPAMembersModal')"
      />
    </b-modal>

    <!-- USAGE MEMBERS MODAL -->
    <b-modal
      id="viewUsageMembersModal"
      ref="viewUsageMembersModal"
      hide-footer
      hide-header
      size="xl"
    >
      <MembersSelector
        :key="selectedAllocation.id"
        :allocation="selectedAllocation"
        :editable="isDraft"
        :excluded_members=true
        @save="setUsageMembers"
        @close="closeModal('viewUsageMembersModal')"
      />
    </b-modal>

    <!-- PLAYLISTS SELECTOR MODAL -->
    <b-modal
      id="playlistSelectorModal"
      ref="playlistSelectorModal"
      hide-footer
      hide-header
      size="xl"
    >
      <PlaylistSelector
        :key="selectedAllocation.id"
        :initial="selectedAllocation.playlists"
        :rule="selectedAllocation.rule"
        @save="setPlaylistSheets"
        @close="closeModal('playlistSelectorModal')"
        allocableOnly
      />
    </b-modal>
    <!-- SHOW PLAYLISTS MODAL -->
    <b-modal
      id="viewPlaylistsModal"
      ref="viewPlaylistsModal"
      hide-footer
      hide-header
      size="sm"
    >
      <div
        v-if="hasPlaylists(selectedAllocation)"
        class="p-4"
      >
        <div
          v-for="playlist in allocationsHash[selectedAllocation.id].playlists"
          :key="playlist.id"
          class="py-2"
        >
          <span class="playlist">{{ playlist.processed_url.split('/').pop() }}</span>
          <div
            v-for="sheet in playlist.sheets"
            :key="sheet.id"
            class="ml-2 py-2 italic"
          >
            <i class="fas fa-level-up-alt rotate-90 mr-2" />
            <span>{{ sheet.name }}</span>
          </div>
        </div>
      </div>
    </b-modal>
    <!-- PRO-RATA FILE UPLOAD MODAL -->
    <b-modal
      id="dropzoneModal"
      ref="dropzoneModal"
      hide-footer
      size="xl"
    >
      <Dropzone
        ref="dropzone"
        method="post"
        type="pro-rata"
        :fund-id="selectedAllocation.id"
        :title="$t('uploadProRataFile')"
        :download-url="downloadURL"
        :upload-url="uploadURL"
        @closeModal="closeModal('dropzoneModal')"
        @error="onDropzoneError"
        @success="onDropzoneSuccess"
      />
    </b-modal>
    <!-- ERRORS MODAL -->
    <b-modal
      id="errorsModal"
      ref="errorsModal"
      hide-footer
      hide-header
      size="xl"
    >
      <div v-for="(errorType, i) in uploadErrors" :key="i">
        <div v-for="(error, j) in errorType" :key="j">
          <span class="medium-bold d-block">{{ error }}</span>
        </div>
      </div>
    </b-modal>
    <b-modal
      id="fundErrorsModal"
      ref="fundErrorsModal"
      hide-footer
      hide-header
      size="xl"
    >
      {{ currentFund.status_string }}
    </b-modal>
  </div>
</template>

<style lang="scss">
  #playlistSelectorModal > .modal-dialog {
    max-width: 1280px !important;
  }
</style>

<style lang="scss" scoped>
.top-text {
  font-size: 22px;
  font-weight: 500;
  margin-top: 40px;
}

.fund-row {
  background-color: white;
}

.btn-lg {
  margin-right: 5px;
  margin-bottom: 10px;
}

#viewPlaylistsModal {
  .playlist {
    font-weight: bold;
    font-size: 1.1rem;
  }
}
</style>

<script>
import { capitalize, find, flatten, get, values } from "lodash"

import MembersSelector from "./MembersSelector"

import { api } from "@/main"
import { mapState } from "vuex"
import { statusTypes } from "@/store/modules/consts"

export default {
  name: "AllocationSelector",

  components: {
    MembersSelector
  },

  data () {
    return {
      allocationsHash: {},
      rules: [],
      rights: [],
      uploadErrors: {},
      selectedAllocation: {},
      statusTypes: statusTypes,
      currentFund: {},
      options: {
        sortable: [],
        columnsClasses: {
          fund: "width-medium",
          rule: "width-medium",
          use_type: "width-medium",
          status: "width-small",
          deduction: "width-small",
          additional_data: "width-large",
        },
        headings: {
          fund: capitalize(this.$t("funds")),
          rule: capitalize(this.$t("rule")),
          use_type: capitalize(this.$t("useType")),
          status: capitalize(this.$t("status")),
          deduction: capitalize(this.$t("deduction_pct")),
          additional_data: capitalize(this.$t("additional_data")),
        },
      }
    }
  },
  methods: {
    getColumns (){
      let columns = ["fund", "rule", "use_type", "status", "additional_data"]
      if (this.user.is_ppl_india_user) {
        columns.splice(4, 0, "deduction")
      }

      return columns
    },
    getRules (row){
      if(Number(row.fund.net_amount)===0 && this.user.is_ppl_india_user){
        return this.rules.filter(item=>item.name === "ipab")
      }else{
        return this.rules.filter(item=>item.name !== "ipab")
      }
    },
    hasRule (row) {
      return Boolean(this.allocationsHash[row.id]?.rule)
    },
    hasUseType (row) {
      return Boolean(this.allocationsHash[row.id]?.right_type)
    },
    hasProRataFile (row) {
      return Boolean(this.allocationsHash[row.id]?.pro_rata_file)
    },
    hasUPAMembers (row) {
      return Boolean(this.allocationsHash[row.id].upa_members?.length)
    },
    hasExcludedMembers (row) {
      return Boolean(this.allocationsHash[row.id].excluded_members?.length)
    },
    hasPlaylists (row) {
      return Boolean(this.allocationsHash[row.id]?.playlists?.length)
    },
    isUPA (row) {
      return this.hasRule(row) && this.allocationsHash[row.id].rule.name === "upa"
    },
    isProRata (row) {
      return this.hasRule(row) && this.allocationsHash[row.id].rule.name === "pro-rata"
    },
    isUsage (row) {
      return this.hasRule(row) && (["usage", "usage-with-unclaims"].includes(this.allocationsHash[row.id].rule.name))
    },
    isIPAB (row) {
      return this.hasRule(row) && this.allocationsHash[row.id].rule.name === "ipab"
    },
    showDropzone (allocation) {
      this.selectedAllocation = allocation
      this.$refs.dropzoneModal.show()
      setTimeout(() => this.$refs.dropzone.$refs.myVueDropzone.removeAllFiles(true), 100)
    },
    onDropzoneError (errors) {
      this.uploadErrors = errors
      this.$refs.errorsModal.show()
    },
    onDropzoneSuccess (file) {
      this.allocationsHash[this.selectedAllocation.id].pro_rata_file = { filename: file.name }
    },
    removeProRataFile (row) {
      this.allocationsHash[row.id].pro_rata_file = null
    },
    closeModal (modal) {
      this.$refs[modal].hide()
    },
    setPlaylistSheets (playlists) {
      const sheets = flatten(playlists.map(playlist => playlist.sheets))
      this.allocationsHash[this.selectedAllocation.id].playlist_sheets = sheets
      this.allocationsHash[this.selectedAllocation.id].playlists = playlists
    },
    setUPAMembers (members) {
      this.allocationsHash[this.selectedAllocation.id].upa_members = members
    },
    setUsageMembers (members) {
      this.allocationsHash[this.selectedAllocation.id].excluded_members = members
    },
    showErrors (row) {
      this.currentFund = row
      this.$refs.fundErrorsModal.show()
    },
    arrayToHash (array, key="id") {
      return array.reduce((hash, item) => {
        hash[item[key]] = item
        return hash
      }, {})
    },
    getAllocationsHash (allocations) {
      allocations = allocations.map(allocation => {

        return {
          ...allocation,
          deduction: allocation.deduction,
          right_type: find(this.rights, right => right.id === allocation.right_type),
          rule: find(this.rules, rule => rule.rule === allocation.rule),
          playlist_sheets: flatten(allocation.playlists.map(playlist => playlist.sheets))
        }
      })
      return this.arrayToHash(allocations)
    },
  },
  computed: {
    ...mapState("soundsys", ["user"]),
    downloadURL () {
      const allocationId = get(this.selectedAllocation, "id")
      const rightTypeId = get(this.allocationsHash[allocationId]?.right_type, "id")

      return `fund-allocation-iterations/${allocationId}/pro-rata/template/?right_type=${rightTypeId}`
    },
    uploadURL () {
      const allocationId = get(this.selectedAllocation, "id")
      const rightTypeId = get(this.allocationsHash[allocationId]?.right_type, "id")

      return `fund-allocation-iterations/${allocationId}/pro-rata/shares/?right_type=${rightTypeId}`
    },
  },
  watch: {
    allocations (newVal) {
      this.allocationsHash = this.getAllocationsHash(newVal)
    },
    allocationsHash: {
      deep: true,
      handler (newVal) {
        this.$emit("change", values(newVal))
      }
    }
  },
  props: {
    allocations: Array,
    isDraft: Boolean
  },
  async mounted () {
    await api.get("distributionrules/").then(response => this.rules = response.data)
    await api.get("rights/").then(response => this.rights = response.data)
    if (this.allocations) {
      this.allocationsHash = this.getAllocationsHash(this.allocations)
    }
  },
}
</script>
